import { createPortal } from 'react-dom';
import React from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, ReferenceArea, LabelList,
} from 'recharts';
import { useP } from '../../i18n/useP';
import './CashPlanChart.css';

/* Graphique du plan de liquidité — repris de la Simulation 12M (décision Olivier
   2026-08-17 : la Simulation part, son graphique reste). Barres encaissements /
   décaissements, courbe de solde, trésorerie N−1 en pointillé, plancher marqué,
   et les cinq indicateurs sous le graphique.

   Aucune saisie ici : tout vient du plan (une seule vérité). */

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const toK = (v) => Math.round(num(v) / 1000);
const fmtK = (v) => toK(v).toLocaleString('de-CH').replace(/’|,/g, "'");

export default function CashPlanChart({ months, labels, enc, dec, solde, opening, n1, floor, win, kpisHost = null, history = [] }) {
  const p = useP();
  /* Fenêtre de lecture : un choix — une année, 12 mois glissants, ou tout. Les
     pastilles vivent dans l'en-tête de la card ; le graphique applique. */
  const shown = win === '12' ? months.slice(0, 12)
    : (win && win !== 'all' ? months.filter((k) => String(k).split('-')[0] === String(win)) : months);

  const idxOf = (k) => months.indexOf(k);
  const planData = shown.map((k) => ({
    label: labels[idxOf(k)],
    enc: Math.abs(num(enc[k])),
    dec: Math.abs(num(dec[k])),
    solde: num(solde[k]),
    n1: n1 && Number.isFinite(n1[idxOf(k)]) ? n1[idxOf(k)] : null,
  }));
  /* L'HISTORIQUE de l'année (demande d'Olivier, 2026-10-12) : en Pro, les mois déjà
     clos s'ajoutent en tête, la seule courbe de trésorerie et celle de l'année
     précédente, sans barres. Les chiffres du plan ne les comptent pas. */
  const data = [
    ...history.map((h) => ({ label: h.label, enc: null, dec: null, solde: h.solde, n1: h.n1, histo: true })),
    ...planData,
  ];
  const hasN1 = (n1 && n1.some((v) => Number.isFinite(v))) || history.some((h) => Number.isFinite(h.n1));
  const soldes = shown.map((k) => num(solde[k]));
  const soldeMin = soldes.length ? Math.min(...soldes) : 0;
  const soldeEnd = soldes.length ? soldes[soldes.length - 1] : 0;
  const encTotal = shown.reduce((s, k) => s + Math.abs(num(enc[k])), 0);
  const encAvg = shown.length ? encTotal / shown.length : 0;
  const showFloor = Number.isFinite(floor) && floor !== 0;
  // Dépassement : au moins un mois sous la ligne de crédit.
  const breached = showFloor && soldeMin < floor;

  /* LES CHIFFRES DU PLAN : solde de départ, d'arrivée, creux, et ce qui entre.
     Ils ne commentent pas le tracé, ils le résument — d'où leur place au-dessus
     de la carte dès qu'on leur en donne une (2026-09-18). */
  const kpis = (
    <div className="cpc__kpis">
      <div className="cpc__kpi"><span>{p('Solde initial')}</span><b>{fmtK(opening)} k</b><em>{p('point de départ')}</em></div>
      <div className={`cpc__kpi${soldeEnd < 0 ? ' is-neg' : ''}`}><span>{p('Solde final')}</span><b>{fmtK(soldeEnd)} k</b><em>{p('fin de période')}</em></div>
      <div className={`cpc__kpi${soldeMin < 0 ? ' is-neg' : ''}`}><span>{p('Solde minimum')}</span><b>{fmtK(soldeMin)} k</b><em>{labels[idxOf(shown[soldes.indexOf(soldeMin)])] || ''}</em></div>
      <div className="cpc__kpi"><span>{p('Encaissements')}</span><b>{fmtK(encTotal)} k</b><em>{p('sur la période')}</em></div>
      <div className="cpc__kpi"><span>{p('Encaissement moyen')}</span><b>{fmtK(encAvg)} k</b><em>{p('par mois')}</em></div>
    </div>
  );

  return (
    <>
    {kpisHost && createPortal(kpis, kpisHost)}
    <div className="cpc">
      <div className="cpc__chart">
        <ResponsiveContainer width="100%" height={420}>
          <ComposedChart data={data} margin={{ top: 12, right: 16, left: 8, bottom: 8 }}>
            <CartesianGrid stroke="#EDE8DF" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#57534E' }}
              axisLine={{ stroke: '#D6D3D1' }} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#57534E' }} axisLine={{ stroke: '#D6D3D1' }}
              tickLine={false} width={68}
              tickFormatter={(v) => fmtK(v)}
              label={{ value: 'kCHF', angle: -90, position: 'insideLeft', offset: 12, style: { fill: '#78716C', fontSize: 10 } }} />
            {/* DEUX zones (variante C, décision Olivier 2026-08-17) :
                 · ambre entre zéro et la ligne de crédit — le crédit se consomme,
                   c'est prévu, le dégradé s'intensifie en approchant de la limite ;
                 · terracotta sous la ligne — dépassement, dessiné SEULEMENT si un
                   mois y tombe, pour que le graphique reste sobre quand tout va bien. */}
            <defs>
              {/* Bande « crédit utilisé » : rouge franc en approchant la limite
                  (retour Olivier 2026-08-17) — le dépassement, lui, est encore
                  plus dense. */}
              <linearGradient id="cpcUsed" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#BA5D4A" stopOpacity={0.08} />
                <stop offset="100%" stopColor="#BA5D4A" stopOpacity={0.34} />
              </linearGradient>
              <linearGradient id="cpcOver" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#993C1D" stopOpacity={0.62} />
                <stop offset="100%" stopColor="#993C1D" stopOpacity={0.38} />
              </linearGradient>
            </defs>
            {showFloor && (
              <ReferenceArea y1={floor} y2={0} fill="url(#cpcUsed)" strokeOpacity={0} />
            )}
            {showFloor && breached && (
              <ReferenceArea y1={Math.min(soldeMin, floor)} y2={floor} fill="url(#cpcOver)" strokeOpacity={0} />
            )}
            <ReferenceLine y={0} stroke="#78716C" strokeWidth={1} />
            {showFloor && (
              <ReferenceLine y={floor} stroke="#BA5D4A" strokeDasharray="4 4" strokeWidth={1.5}
                label={{
                  value: `${p('Ligne de crédit')} ${fmtK(floor)} k`,
                  // Au-DESSUS du trait, calé à gauche (retour Olivier 2026-08-17).
                  position: 'top', offset: 6,
                  style: { fill: '#BA5D4A', fontSize: 11, fontWeight: 600, textAnchor: 'start' },
                }} />
            )}
            <Tooltip content={({ active, payload }) => {
              if (!active || !payload || !payload.length) return null;
              const d = payload[0].payload;
              return (
                <div className="cpc__tip">
                  <div className="cpc__tip-m">{d.label}</div>
                  {!d.histo && <div className="cpc__tip-r"><span className="sw" style={{ background: '#5C7A3E' }} />{p('Encaissements')}<b>{fmtK(d.enc)} k</b></div>}
                  {!d.histo && <div className="cpc__tip-r"><span className="sw" style={{ background: '#BA5D4A' }} />{p('Décaissements')}<b>−{fmtK(d.dec)} k</b></div>}
                  {d.n1 != null && <div className="cpc__tip-r"><span className="sw sw--n1" />{p('Trésorerie N−1')}<b>{fmtK(d.n1)} k</b></div>}
                  <div className="cpc__tip-t">{p('Solde de trésorerie')}<b>{fmtK(d.solde)} k</b></div>
                </div>
              );
            }} />
            <Bar dataKey="enc" name={p('Encaissements')} fill="#9DC076" stroke="#5C7A3E" fillOpacity={0.5} barSize={22} />
            <Bar dataKey="dec" name={p('Décaissements')} fill="#E6B5A6" stroke="#BA5D4A" fillOpacity={0.5} barSize={22} />
            {hasN1 && (
              <Line type="monotone" dataKey="n1" name={p('Trésorerie N−1')} stroke="#2B203E"
                strokeDasharray="5 4" strokeWidth={1.5} dot={{ r: 2 }} connectNulls />
            )}
            <Line type="monotone" dataKey="solde" name={p('Solde de trésorerie')} stroke="#2B203E" strokeWidth={2.5}
              dot={(dp) => <circle key={dp.key} cx={dp.cx} cy={dp.cy} r={4}
                fill={dp.payload.solde < 0 ? '#BA5D4A' : '#2B203E'} stroke="none" />}>
              <LabelList dataKey="solde" position="bottom" offset={10}
                formatter={(v) => (v < 0 ? `${fmtK(v)} k` : '')}
                style={{ fill: '#BA5D4A', fontSize: 10, fontWeight: 600 }} />
            </Line>
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="cpc__legend">
        <span><i className="cpc__sw" style={{ background: '#9DC076', borderColor: '#5C7A3E' }} />{p('Encaissements')}</span>
        <span><i className="cpc__sw" style={{ background: '#E6B5A6', borderColor: '#BA5D4A' }} />{p('Décaissements')}</span>
        <span><i className="cpc__ln" />{p('Solde de trésorerie')}</span>
        {hasN1 && <span><i className="cpc__ln cpc__ln--dash" />{p('Trésorerie N−1 (bilan)')}</span>}
        {showFloor && <span className="cpc__floor"><i className="cpc__ln cpc__ln--dash cpc__ln--red" />{p('Ligne de crédit')} {fmtK(floor)} k</span>}
        {showFloor && <span className="cpc__zone cpc__zone--used">{p('crédit utilisé')}</span>}
        {breached && <span className="cpc__zone cpc__zone--over">{p('dépassement')}</span>}
      </div>

      {/* LES TUILES MONTENT AU-DESSUS DE LA CARTE (2026-09-18) quand on leur
          donne un hôte : ce sont les CHIFFRES du plan, pas une légende du
          tracé, et sous la courbe il fallait descendre pour lire le solde
          final. Sans hôte (parcours du Prévisionnel), elles restent en pied. */}
      {!kpisHost && kpis}
    </div>
    </>
  );
}
